/**
 * Signing out, which always ends our session and ends the identity provider's only for
 * MIKE Cloud.
 *
 * A signed-in person here holds up to three independent sessions: ours, tokens the
 * service worker keeps in memory; MIKE Cloud's, a cookie on the login host; and
 * Microsoft's, a cookie on `login.microsoftonline.com`. Clearing only ours used to make
 * the sign-out misleading. It looked complete, and the next sign-in was one silent click
 * back into the same account, which is wrong on any shared machine and gives someone who
 * wants a different account no way in at all.
 *
 * Entra fixes that on the way back in rather than on the way out. Its sign-out clears
 * ours along with the remembered account and provider, and leaves Microsoft's session
 * alone, because that is the session a colleague works in all day. The next sign-in then
 * remembers nobody, so Entra is asked for its account picker (`authorizeExtras`). Ending
 * Microsoft's session as well signed people out of Outlook and the portal, and on a
 * DHI-managed device Windows signed them straight back in anyway (adr/0038).
 *
 * MIKE Cloud forwards no `prompt` to Microsoft, so its sign-out still ends the other two,
 * as a round trip in this tab: our session is cleared, a marker is left for the page that
 * comes back, the tab leaves for the identity provider and returns to the email step
 * saying "You are signed out". No second tab, nothing to close.
 *
 * MIKE Cloud's `/logout` honours `post_logout_redirect_uri` only alongside an
 * `id_token_hint`. This file used to say the SPA could not supply one because the service
 * worker kept the id token out of JavaScript, and ran the sign-out in a second tab that
 * was closed again when this document regained focus. That was a belief, not a
 * measurement: the worker replaces the access and refresh tokens with placeholders and
 * passes the id token through, so the hint is there for the taking. Measured 2026-09-03
 * with a real session: `/logout?id_token_hint=...&post_logout_redirect_uri=<root>` went
 * to Microsoft and came back to `/?state=probe`. What it does not do is forward a
 * `logout_hint`, so Microsoft still asks which account to sign out of, one click, before
 * the chain returns. Measured 2026-08-27: that picker is a page and not a redirect, and
 * nothing is signed out until an account is clicked.
 *
 * The second tab remains only as the fallback for a MIKE Cloud session with no id token,
 * which does not happen in practice. It is left open until this document is looked at
 * again, which is the one moment we know the person is done with it.
 */

import { OidcClient } from '@axa-fr/react-oidc';
import { getActiveProvider } from './config';
import { forgetAccount } from './lastAccount';
import { forgetProvider } from './lastProvider';
import {
  endsSessionInPlace,
  platformSignOutUrl,
  signOutEndsPlatformSession,
  signOutReturnUrl,
} from './oidc';

/**
 * Left in `sessionStorage` before an in-place sign-out leaves the page, read once by the
 * page that comes back. `sessionStorage` because the round trip stays in this tab and the
 * flag must not outlive it. Without this the return lands on the email step with nothing
 * to say a sign-out just happened, which reads as the app having forgotten the person.
 */
const SIGNED_OUT_KEY = 'mikeai.signed-out';

function sessionStore(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function markSignedOutForReturn(store: Storage | null = sessionStore()): void {
  try {
    store?.setItem(SIGNED_OUT_KEY, '1');
  } catch {
    // The return then reads as a plain front door, which is untidy rather than wrong.
  }
}

/** Whether the previous page in this tab left by signing out. Consumes the marker. */
export function takeSignedOutOnReturn(store: Storage | null = sessionStore()): boolean {
  try {
    const was = store?.getItem(SIGNED_OUT_KEY) === '1';
    if (was) store?.removeItem(SIGNED_OUT_KEY);
    return was;
  } catch {
    return false;
  }
}

/**
 * How long after opening the tab we refuse to close it.
 *
 * Only a guard against the focus handover itself reporting this document visible again
 * before the new tab has taken over. There is deliberately no upper bound any more: the
 * step in that tab is interactive and untimed, so any deadline we invent is a deadline
 * for a person reading a page, and the one that shipped first was four seconds.
 */
const PLATFORM_SIGN_OUT_MIN_MS = 1500;

/** Where the platform half of a sign-out has got to. */
export type PlatformSignOutState = 'opening' | 'done' | 'blocked';

type SignedOutHandler = () => void;
type PlatformHandler = (state: PlatformSignOutState) => void;

let handler: SignedOutHandler | null = null;
let platformHandler: PlatformHandler | null = null;

export function setSignedOutHandler(fn: SignedOutHandler | null): void {
  handler = fn;
}

export function setPlatformSignOutHandler(fn: PlatformHandler | null): void {
  platformHandler = fn;
}

/**
 * Announce that this browser is signed out of DHI Docs AI.
 *
 * Called by `signOutEverywhere` below, and by `main.tsx` when the library reports a
 * sign-out performed in another tab. That second caller is the reason this is exported:
 * without it, a sign-out in one tab left every other tab on the library's dead-end
 * "Session timed out" screen, which is both wrong about what happened and unhelpful
 * about what to do next.
 */
export function notifySignedOut(): void {
  handler?.();
}

/** Forget who signed in last and with which provider, which every sign-out does. */
function forgetSignedInAccount(): void {
  forgetAccount();
  // And the provider, so that what this browser shows after a sign-out does not depend on
  // whether the page has been reloaded since.
  //
  // These two used to have deliberately different lifetimes: the account went and the
  // provider stayed, on the reasoning that signing out is not usually a decision to sign
  // in somewhere else. That was written when the alternative was a two-button choice
  // screen, and it stopped being true when the front door became an address field. Keeping
  // the provider meant a sign-out rendered the email step in memory and then, on the next
  // reload, resolved the remembered provider and rendered the per-provider screen instead:
  // "Sign in" beside "Use a different account", after a sign-out, with no way to tell why.
  //
  // The general shape is worth remembering, because it is the same defect as the hidden
  // escape hatch: two pieces of state that are supposed to describe one situation, cleared
  // on different schedules, so the screen contradicts itself depending on which one is
  // consulted. Retyping an address is what the front door asks of everybody anyway, and it
  // routes back to the same provider, so nothing is lost by forgetting.
  forgetProvider();
}

/**
 * End the identity provider's session and forget the account.
 *
 * In this tab when the provider can return to us, which is MIKE Cloud given its id token;
 * in a second tab otherwise. Synchronous, and it must stay that way: browsers allow
 * `window.open` only while a click is still being handled, so anything awaited before
 * this point turns the fallback tab into a blocked popup.
 */
export function openPlatformSignOut(idToken: string | null = null): void {
  const provider = getActiveProvider();
  forgetSignedInAccount();

  if (provider && endsSessionInPlace(provider, idToken)) {
    // A round trip rather than a tab: the provider ends its session and returns to our
    // app root, which boots to the email step. Microsoft shows its "which account" picker
    // in this tab on the way, and still returns here after the click.
    window.location.assign(
      platformSignOutUrl(provider, {
        returnTo: signOutReturnUrl(),
        idTokenHint: idToken,
      }),
    );
    return;
  }

  // Deliberately without `noopener,noreferrer`: both of those make `window.open` return
  // null whether the tab opened or was blocked, and a caller that cannot tell the
  // difference tells the user their session survived when it did not. They would also
  // put the handle's right to close the tab in doubt, which is the whole point of
  // keeping it. The page on the other end is the identity provider itself.
  const tab = window.open(platformSignOutUrl(), '_blank');
  if (!tab) {
    platformHandler?.('blocked');
    return;
  }
  platformHandler?.('opening');
  closeWhenBack(tab);
}

/** Close `tab` when this document is looked at again, and not before. */
function closeWhenBack(tab: Window): void {
  let settled = false;

  const finish = () => {
    if (settled) return;
    settled = true;
    document.removeEventListener('visibilitychange', onVisible);
    try {
      // Unconditionally, without first reading `tab.closed`, which would only add a
      // second console line. Microsoft's page carries `Cross-Origin-Opener-Policy:
      // same-origin`, so Chrome logs "policy would block the window.close call" here on
      // every sign-out. Measured on 2026-08-26: the handle still answers and the tab
      // still closes, so the line is noise rather than a failure, and closing an
      // already-closed window does nothing.
      tab.close();
    } catch {
      // Refused, or already gone. Untidy rather than broken.
    }
    platformHandler?.('done');
  };

  const opened = Date.now();
  const onVisible = () => {
    if (document.visibilityState !== 'visible') return;
    const waited = Date.now() - opened;
    if (waited >= PLATFORM_SIGN_OUT_MIN_MS) finish();
    else window.setTimeout(finish, PLATFORM_SIGN_OUT_MIN_MS - waited);
  };

  document.addEventListener('visibilitychange', onVisible);
}

/**
 * End our session and, for MIKE Cloud, the platform's, and show our own signed-out screen
 * rather than the platform's.
 */
export function signOutEverywhere(): void {
  const provider = getActiveProvider();
  const client = OidcClient.getOrThrow();
  if (provider && !signOutEndsPlatformSession(provider)) {
    // Entra: Microsoft's session stays, and forgetting the account is what makes the next
    // sign-in ask for the account picker.
    forgetSignedInAccount();
    void client.clearSessionAsync().then(notifySignedOut);
    return;
  }
  // Read before the session is cleared, since clearing is what takes it away.
  const idToken = client.tokens?.idToken ?? null;
  if (provider && endsSessionInPlace(provider, idToken)) {
    // Ours first, then leave. The page that comes back is a fresh load, so it is told
    // through storage rather than through `notifySignedOut`, which would be announcing to
    // a page about to unload. `finally` rather than `then`: a session that would not
    // clear is no reason to leave the provider's cookie alive as well.
    markSignedOutForReturn();
    void client.clearSessionAsync().finally(() => openPlatformSignOut(idToken));
    return;
  }
  openPlatformSignOut();
  // Clears tokens, user info and the service worker's copy, and broadcasts to other
  // clients in this tab, without contacting the identity provider.
  void client.clearSessionAsync().then(notifySignedOut);
}
